import React from 'react';
import * as T from '@radix-ui/react-tooltip';
import * as M from '@radix-ui/react-dropdown-menu';
import * as P from '@radix-ui/react-popover';
import * as C from '@radix-ui/react-collapsible';
import { Check, ChevronRight } from 'lucide-react';
import { cx } from './cx';
import { foco } from './primitives';

/**
 * Camada de sobreposição. Antes o produto usava `title=` do navegador como dica (atraso de ~1s,
 * sem estilo, invisível no toque) e reimplementava menu à mão, sem fechar ao clicar fora e sem
 * teclado. Aqui tudo é Radix com a pele do kit.
 */

const painel =
  'z-50 rounded-lg border border-border bg-surface p-1 shadow-elev-pop est-content';

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */

/** Um provider só, no topo da aplicação, define o atraso de todas as dicas. */
export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return (
    <T.Provider delayDuration={250} skipDelayDuration={300}>
      {children}
    </T.Provider>
  );
}

export function Tooltip({
  content,
  children,
  side = 'top',
  atalho,
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  atalho?: string;
}) {
  if (!content) return <>{children}</>;
  return (
    <T.Root>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="est-content z-50 flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text shadow-elev-2"
        >
          {content}
          {atalho ? <span className="font-mono-data text-faint">{atalho}</span> : null}
          <T.Arrow className="fill-surface" />
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

/* ─── Menu suspenso ───────────────────────────────────────────────────────── */

export interface ItemMenu {
  label: string;
  onSelect?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  atalho?: string;
  destrutivo?: boolean;
  disabled?: boolean;
  marcado?: boolean;
  /** Linha divisória ANTES deste item. */
  separarAntes?: boolean;
}

const itemBase =
  'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm outline-none ' +
  'data-[highlighted]:bg-surface-2 data-[disabled]:pointer-events-none data-[disabled]:opacity-45';

export function DropdownMenu({
  trigger,
  itens,
  align = 'end',
  className,
}: {
  trigger: React.ReactNode;
  itens: ItemMenu[];
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <M.Root>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content align={align} sideOffset={6} className={cx(painel, 'min-w-[11rem]', className)}>
          {itens.map((it, i) => (
            <React.Fragment key={`${it.label}-${i}`}>
              {it.separarAntes ? <M.Separator className="my-1 h-px bg-border" /> : null}
              <M.Item
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cx(itemBase, it.destrutivo ? 'text-dead' : 'text-text')}
              >
                {it.icon ? <it.icon className="h-4 w-4 shrink-0 opacity-80" /> : null}
                <span className="flex-1 truncate">{it.label}</span>
                {it.marcado ? <Check className="h-3.5 w-3.5 text-brand" /> : null}
                {it.atalho ? <span className="font-mono-data text-[0.7rem] text-faint">{it.atalho}</span> : null}
              </M.Item>
            </React.Fragment>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

/* ─── Popover ─────────────────────────────────────────────────────────────── */

export function Popover({
  trigger,
  children,
  align = 'start',
  className,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <P.Root>
      <P.Trigger asChild>{trigger}</P.Trigger>
      <P.Portal>
        <P.Content align={align} sideOffset={6} className={cx(painel, 'p-3', className)}>
          {children}
          <P.Arrow className="fill-surface" />
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

/* ─── Collapsible ─────────────────────────────────────────────────────────── */

export function Collapsible({
  title,
  children,
  defaultOpen,
  right,
  className,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  right?: React.ReactNode;
  className?: string;
}) {
  return (
    <C.Root defaultOpen={defaultOpen} className={cx('rounded-lg border border-border', className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <C.Trigger className={cx('group flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium text-text', foco)}>
          <ChevronRight className="h-4 w-4 shrink-0 text-faint transition-transform group-data-[state=open]:rotate-90" />
          <span className="truncate">{title}</span>
        </C.Trigger>
        {right}
      </div>
      <C.Content className="est-collapse overflow-hidden border-t border-border">
        <div className="px-3 py-3">{children}</div>
      </C.Content>
    </C.Root>
  );
}
